import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../containers/ApiContainer";
import { scrollSx } from "../../utils";

type Props = {};

export const AuthorizedAccounts: React.FC<Props> = () => {
  const { t } = useTranslation();
  const [testEmail, setTestEmail] = React.useState<string>("");
  const [testResult, setTestResult] = React.useState<boolean>();
  const [emailOrDomain, setEmailOrDomain] = React.useState<string>("");
  const [authorizedAccounts, setAuthorizedAccounts] = React.useState<
    { email_or_domain: string }[]
  >([]);

  // Password whitelist state
  const [whitelistEmail, setWhitelistEmail] = React.useState<string>("");
  const [whitelistTestEmail, setWhitelistTestEmail] = React.useState<string>("");
  const [whitelistTestResult, setWhitelistTestResult] = React.useState<boolean>();
  const [passwordWhitelist, setPasswordWhitelist] = React.useState<
    { email: string; reason?: string; created_at: string }[]
  >([]);

  const { postgrest } = useApi();

  // Fetch functions with error handling
  const fetchAuthorizedAccounts = React.useCallback(async () => {
    const result = await postgrest.from("authorized_account").select("*");
    setAuthorizedAccounts(result.data || []);
  }, [postgrest]);

  const fetchPasswordWhitelist = React.useCallback(async () => {
    const result = await postgrest
      .from("password_auth_whitelist")
      .select("*")
      .order("created_at", { ascending: false });
    setPasswordWhitelist(result.data || []);
  }, [postgrest]);

  // Handler: Delete authorized account
  const handleDeleteAuthorizedAccount = React.useCallback(
    async (emailOrDomain: string) => {
      await postgrest
        .from("authorized_account")
        .delete()
        .eq("email_or_domain", emailOrDomain);
      await fetchAuthorizedAccounts();
    },
    [postgrest, fetchAuthorizedAccounts]
  );

  // Handler: Add authorized account
  const handleAddAuthorizedAccount = React.useCallback(async () => {
    if (emailOrDomain.length < 3) return;

    await postgrest
      .from("authorized_account")
      .insert({ email_or_domain: emailOrDomain });
    await fetchAuthorizedAccounts();
    setEmailOrDomain("");
  }, [emailOrDomain, postgrest, fetchAuthorizedAccounts]);

  // Handler: Test authorized account
  const handleTestAuthorizedAccount = React.useCallback(async () => {
    setTestResult(undefined);
    const result = await postgrest.rpc("is_authorized", { email: testEmail });
    setTestResult(result.data as unknown as boolean);
  }, [testEmail, postgrest]);

  // Handler: Delete password whitelist entry
  const handleDeleteWhitelistEntry = React.useCallback(
    async (email: string) => {
      await postgrest
        .from("password_auth_whitelist")
        .delete()
        .eq("email", email);
      await fetchPasswordWhitelist();
    },
    [postgrest, fetchPasswordWhitelist]
  );

  // Handler: Add password whitelist entry
  const handleAddWhitelistEntry = React.useCallback(async () => {
    if (whitelistEmail.length < 3) return;

    await postgrest.from("password_auth_whitelist").insert({
      email: whitelistEmail,
      reason: t("screens.authorizedAccounts.defaultReason"),
    });
    await fetchPasswordWhitelist();
    setWhitelistEmail("");
  }, [whitelistEmail, postgrest, fetchPasswordWhitelist, t]);

  // Handler: Test password whitelist
  const handleTestWhitelist = React.useCallback(async () => {
    setWhitelistTestResult(undefined);
    const result = await postgrest.rpc("is_password_auth_whitelisted", {
      p_email: whitelistTestEmail,
    });
    setWhitelistTestResult(result.data as unknown as boolean);
  }, [whitelistTestEmail, postgrest]);

  React.useEffect(() => {
    fetchAuthorizedAccounts();
    fetchPasswordWhitelist();
  }, [fetchAuthorizedAccounts, fetchPasswordWhitelist]);

  return (
    <Stack direction="column" spacing={1} sx={{ width: 400 }}>
      <List
        subheader={
          <>
            <ListSubheader component="div" id="nested-list-subheader">
              {t("screens.authorizedAccounts.validEmailsTitle")}
            </ListSubheader>
            <Divider />
          </>
        }
      >
        <Box
          sx={{
            maxHeight: 500,
            overflow: "auto",
            ...scrollSx,
          }}
        >
          {authorizedAccounts.map((authorizedAccount) => (
            <ListItem
              key={authorizedAccount.email_or_domain}
              secondaryAction={
                <IconButton
                  onClick={() =>
                    handleDeleteAuthorizedAccount(
                      authorizedAccount.email_or_domain
                    )
                  }
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText primary={authorizedAccount.email_or_domain} />
            </ListItem>
          ))}
        </Box>
      </List>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          placeholder={t("screens.authorizedAccounts.newEmailPlaceholder")}
          size="small"
          onChange={(e) => setEmailOrDomain(e.target.value)}
          value={emailOrDomain}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleAddAuthorizedAccount}
        >
          {t("screens.authorizedAccounts.addButton")}
        </Button>
      </Stack>
      <Stack direction="column" spacing={1}>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("screens.authorizedAccounts.emailToTestPlaceholder")}
            onChange={(e) => {
              setTestResult(null);
              setTestEmail(e.target.value);
            }}
            value={testEmail}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleTestAuthorizedAccount}
          >
            {t("screens.authorizedAccounts.testButton")}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          {testResult === true && <CheckIcon sx={{ color: "green" }} />}
          {testResult === false && <CloseIcon sx={{ color: "red" }} />}
          <Typography>
            {testResult === true && t("screens.authorizedAccounts.testPassed")}
            {testResult === false && t("screens.authorizedAccounts.testFailed")}
          </Typography>
        </Stack>
      </Stack>

      {/* Password Authentication Whitelist Section */}
      <Stack direction="column" spacing={1} sx={{ width: 400, mt: 3 }}>
        <List
          subheader={
            <>
              <ListSubheader component="div" id="password-whitelist-subheader">
                {t("screens.authorizedAccounts.passwordWhitelistTitle")}
              </ListSubheader>
              <Divider />
            </>
          }
        >
          <Box
            sx={{
              maxHeight: 500,
              overflow: "auto",
              ...scrollSx,
            }}
          >
            {passwordWhitelist.map((item) => (
              <ListItem
                key={item.email}
                secondaryAction={
                  <IconButton onClick={() => handleDeleteWhitelistEntry(item.email)}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={item.email}
                  secondary={item.reason}
                />
              </ListItem>
            ))}
          </Box>
        </List>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            placeholder={t("screens.authorizedAccounts.newEmailWhitelistPlaceholder")}
            size="small"
            onChange={(e) => setWhitelistEmail(e.target.value)}
            value={whitelistEmail}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAddWhitelistEntry}
          >
            {t("screens.authorizedAccounts.addButton")}
          </Button>
        </Stack>
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("screens.authorizedAccounts.emailToTestPlaceholder")}
              onChange={(e) => {
                setWhitelistTestResult(null);
                setWhitelistTestEmail(e.target.value);
              }}
              value={whitelistTestEmail}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleTestWhitelist}
            >
              {t("screens.authorizedAccounts.testButton")}
            </Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            {whitelistTestResult === true && <CheckIcon sx={{ color: "green" }} />}
            {whitelistTestResult === false && <CloseIcon sx={{ color: "red" }} />}
            <Typography>
              {whitelistTestResult === true && t("screens.authorizedAccounts.whitelistTestPassed")}
              {whitelistTestResult === false && t("screens.authorizedAccounts.whitelistTestFailed")}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
